/**
 * tm-telemetry.js — production error log sender (theme v24.5, 2026-09-28)
 * ---------------------------------------------------------------------------
 * Sends failures, errors and a few attempt counts to our own log
 * (worker teddys-telemetry -> D1 teddys-telemetry), so a deploy can be checked
 * for new problems within minutes instead of waiting on GA4.
 *
 * Troubleshooting only. Nothing personal is sent: no photos, file names,
 * customer details or full URLs. The worker stores only the event names on its
 * allowlist and drops everything else.
 *
 * API: window.tmLog(eventName, params)   never throws, never blocks.
 *
 * Delivery: events are batched for a second, then POSTed as text/plain (a
 * simple request, so no CORS preflight) with keepalive so a batch survives the
 * page closing. A batch that can't be sent (offline, dropped connection) is
 * kept in localStorage and sent on the next page view, which is how the "no
 * signal at all" failures still arrive.
 *
 * v24.6 (2026-09-29):
 * - Test mode. ?tm_test=1 on any page turns it on for this browser, ?tm_test=0
 *   turns it off. Kept in localStorage AND a cookie, because in-app browsers
 *   (Facebook, Instagram) sometimes lose one of them between pages. While it's
 *   on, every event carries test: 1, a "TEST MODE" badge shows, and
 *   window.tmIsTest() tells the uploader to flag its worker calls too.
 * - Automation. Browsers driven by a script (navigator.webdriver, headless
 *   Chrome, crawlers) still send, but every event carries bot: 1 so they can
 *   be left out of counts. Tagged rather than dropped: a dropped event can't
 *   show us that the detection is wrong.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';
  if (window.tmLog) return;

  var ENDPOINT = 'https://teddys-telemetry.mark-hatlan.workers.dev/e';
  var TEST_KEY = 'tm_test';
  var TEST_COOKIE_DAYS = 30;

  // --- Test mode -----------------------------------------------------------
  function readTestFlag() {
    try { if (localStorage.getItem(TEST_KEY) === '1') return true; } catch (e) { /* blocked */ }
    try { return /(?:^|;\s*)tm_test=1(?:;|$)/.test(document.cookie); } catch (e) { return false; }
  }

  function writeTestFlag(on) {
    try {
      if (on) localStorage.setItem(TEST_KEY, '1');
      else localStorage.removeItem(TEST_KEY);
    } catch (e) { /* blocked: the cookie still carries it */ }
    try {
      document.cookie = on
        ? 'tm_test=1; path=/; max-age=' + (TEST_COOKIE_DAYS * 86400) + '; SameSite=Lax'
        : 'tm_test=; path=/; max-age=0; SameSite=Lax';
    } catch (e) { /* no-op */ }
  }

  var testParam = (location.search.match(/[?&]tm_test=([01])(?:&|$)/) || [])[1];
  if (testParam) writeTestFlag(testParam === '1');
  var IS_TEST = readTestFlag();
  window.tmIsTest = function () { return IS_TEST; };

  function showTestBadge() {
    try {
      if (document.getElementById('tm-test-badge')) return;
      var b = document.createElement('div');
      b.id = 'tm-test-badge';
      b.textContent = 'TEST MODE';
      // pointer-events: none, so it can never cover a button.
      b.style.cssText = 'position:fixed;top:calc(4px + env(safe-area-inset-top, 0px));right:4px;' +
        'z-index:2147483647;pointer-events:none;background:#d0021b;color:#fff;' +
        'font:700 10px/1 -apple-system,BlinkMacSystemFont,sans-serif;letter-spacing:.05em;' +
        'padding:4px 6px;border-radius:3px;opacity:.85;';
      document.body.appendChild(b);
    } catch (e) { /* the badge is a reminder, never a reason to break the page */ }
  }
  if (IS_TEST) {
    if (document.body) showTestBadge();
    else document.addEventListener('DOMContentLoaded', showTestBadge);
  }

  // --- Device code -----------------------------------------------------------
  // v24.7 (moved here from uploader.js so every page's events carry it, cart
  // included). Compact, deliberately coarse, e.g. fb-ios-ram4-c6-dpr3: GA4
  // collapses high-cardinality values into "(other)", so nothing is per-device.
  var CLIENT_ENV = (function () {
    try {
      var ua = navigator.userAgent || '';
      var app = 'other';
      if (/FBAN|FBAV|FB_IAB|FBIOS/.test(ua)) app = 'fb';
      else if (/Instagram/.test(ua)) app = 'ig';
      else if (/; wv\)|\bwv\b/.test(ua)) app = 'webview';
      else if (/EdgA?\//.test(ua)) app = 'edge';
      else if (/SamsungBrowser/.test(ua)) app = 'samsung';
      else if (/CriOS|Chrome\//.test(ua)) app = 'chrome';
      else if (/Safari\//.test(ua)) app = 'safari';

      var os = 'other';
      if (/iPhone|iPad|iPod/.test(ua)) os = 'ios';
      else if (/Android/.test(ua)) os = 'android';
      else if (/Macintosh/.test(ua)) os = 'mac';
      else if (/Windows/.test(ua)) os = 'win';

      // iPhone browsers never expose memory, but the Facebook and Instagram
      // apps name the model in their user agent ("FBDV/iPhone14,5",
      // "(iPhone14,5; iOS 17_5"). Only the memory group is kept, never the
      // model: ram4 = 4GB or less (iPhone 13 and older, 12/13 Pro excepted),
      // ram6 = 6GB (12/13 Pro, 14, 15), ram8 = 8GB or more (15 Pro, 16 on).
      var mem = navigator.deviceMemory ? ('mem' + navigator.deviceMemory) : 'memNA';
      var hw = ua.match(/iPhone(\d+),(\d+)/);
      if (mem === 'memNA' && hw) {
        var major = +hw[1], minor = +hw[2];
        if (major >= 16) mem = 'ram8';
        else if (major === 15) mem = 'ram6';
        else if (major === 14) mem = (minor === 2 || minor === 3 || minor === 7 || minor === 8) ? 'ram6' : 'ram4';
        else if (major === 13) mem = (minor === 3 || minor === 4) ? 'ram6' : 'ram4';
        else mem = 'ram4';
      }
      var cores = navigator.hardwareConcurrency ? ('c' + navigator.hardwareConcurrency) : 'cNA';
      var dpr = 'dpr' + Math.round(window.devicePixelRatio || 1);

      // Chromium exposes a JS heap ceiling; Safari does not.
      var heap = '';
      if (window.performance && performance.memory && performance.memory.jsHeapSizeLimit) {
        var mb = Math.round(performance.memory.jsHeapSizeLimit / 1048576);
        var steps = [256, 512, 1024, 2048, 4096], b = 'gt4096';
        for (var i = 0; i < steps.length; i++) if (mb <= steps[i]) { b = String(steps[i]); break; }
        heap = '-h' + b;
      }
      return (app + '-' + os + '-' + mem + '-' + cores + '-' + dpr + heap).slice(0, 90);
    } catch (e) {
      return 'unknown';
    }
  })();
  window.tmClientEnv = CLIENT_ENV;

  // --- Automation ----------------------------------------------------------
  var IS_BOT = (function () {
    try {
      if (navigator.webdriver) return true;
      return /HeadlessChrome|bot\b|crawler|spider|Lighthouse|PhantomJS|facebookexternalhit/i
        .test(navigator.userAgent || '');
    } catch (e) { return false; }
  })();
  var QUEUE_KEY = 'tm_log_queue';
  var QUEUE_MAX = 40;        // oldest dropped beyond this
  var BATCH_MAX = 25;        // matches the worker's per-request cap
  var FLUSH_DELAY_MS = 1000;

  var pending = [];
  var timer = null;

  function themeId() {
    try { var S = window.Shopify; return String((S && S.theme && S.theme.id) || ''); }
    catch (e) { return ''; }
  }

  function readQueue() {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]') || []; }
    catch (e) { return []; }
  }

  function writeQueue(list) {
    try {
      if (list.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(list.slice(-QUEUE_MAX)));
      else localStorage.removeItem(QUEUE_KEY);
    } catch (e) { /* storage full or blocked: the events are lost, nothing breaks */ }
  }

  function keep(events) {
    for (var i = 0; i < events.length; i++) events[i].queued = 1;
    writeQueue(readQueue().concat(events));
  }

  function post(events) {
    if (!events.length) return;
    if (navigator.onLine === false) { keep(events); return; }
    try {
      // no-cors: we never read the reply, and it means only a real network
      // failure rejects. A CORS mismatch can't make a delivered batch look
      // failed and get queued twice.
      fetch(ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ events: events }),
        keepalive: true
      }).catch(function () { keep(events); });
    } catch (e) {
      keep(events);
    }
  }

  function flush() {
    clearTimeout(timer);
    timer = null;
    while (pending.length) post(pending.splice(0, BATCH_MAX));
  }

  window.tmLog = function (name, params) {
    try {
      var e = { event: String(name), client_ts: new Date().toISOString(), theme_id: themeId() };
      if (window.UPLOADER_BUILD) e.build = String(window.UPLOADER_BUILD);
      if (params) {
        for (var k in params) {
          if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
          var v = params[k];
          if (typeof v === 'number' || typeof v === 'boolean') e[k] = v;
          else if (v != null) e[k] = String(v).slice(0, 200);
        }
      }
      if (!e.client_env) e.client_env = CLIENT_ENV;
      if (IS_TEST) e.test = 1;
      if (IS_BOT) e.bot = 1;
      pending.push(e);
      if (pending.length >= BATCH_MAX) flush();
      else if (!timer) timer = setTimeout(flush, FLUSH_DELAY_MS);
    } catch (err) { /* logging must never break the page */ }
  };

  // Send what's waiting when the page is hidden or closed.
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });

  // Deliver anything a previous page view couldn't send.
  function drainQueue() {
    var q = readQueue();
    if (!q.length || navigator.onLine === false) return;
    writeQueue([]);
    while (q.length) post(q.splice(0, BATCH_MAX));
  }
  setTimeout(drainQueue, 1500);
  window.addEventListener('online', drainQueue);

  // Which script threw: file name and line, e.g. "uploader.js:3771". Path and
  // query dropped (theme asset URLs carry a version string).
  window.tmErrorSource = function (ev) {
    try {
      if (!ev || !ev.filename) return '';
      var file = String(ev.filename).split('?')[0].split('#')[0].split('/').pop() || 'inline';
      return (file + ':' + (ev.lineno || 0)).slice(0, 80);
    } catch (e) { return ''; }
  };

  // Cart page: any script crash. Product pages are already covered by the
  // uploader's own uploader_js_error. Capped so one noisy app can't flood it.
  if (/^\/cart\/?$/.test(location.pathname)) {
    var sent = 0;
    var report = function (kind, msg, src) {
      if (sent >= 3) return;
      sent++;
      var p = { fail_stage: 'js_' + kind, error_detail: String(msg || '').slice(0, 100) };
      if (src) p.src = src;
      window.tmLog('cart_js_error', p);
    };
    window.addEventListener('error', function (ev) {
      // Resource load failures (img, script) also arrive here with no message;
      // thumbnails are reported separately, so only real script errors count.
      if (ev && ev.message) report('error', ev.message, window.tmErrorSource(ev));
    });
    window.addEventListener('unhandledrejection', function (ev) {
      var r = ev && ev.reason;
      report('rejection', r && (r.message || r.name));
    });
  }
})();
